/**
 * АВАТАР — НАРУЖУ УХОДИТ ТОЛЬКО JPEG.
 *
 * ПОВОД (замер 2026-09-26, не догадка). PNG-аватар приходил в
 * push-уведомления на iOS монограммой вместо картинки. Тому же профилю
 * заменили аватар на JPEG — уведомления починились СРАЗУ, без пересборки
 * клиента. Значит дело не в коде уведомлений, а в формате картинки на
 * сервере. Все 13 профилей переведены в JPEG вручную; этот модуль нужен,
 * чтобы новые загрузки не вернули проблему.
 *
 * ПОЧЕМУ КОНВЕРТАЦИЯ, А НЕ ЗАПРЕТ.
 *
 * Простой запрет «только JPEG» стоит дёшево в коде и дорого для человека:
 * скриншот в macOS сохраняется в PNG, снимок с iPhone — в HEIC, картинка
 * из Telegram — в WebP. То есть самый частый способ поставить аватар
 * («сделал скриншот, перетащил») перестал бы работать вовсе, и человек
 * получил бы ошибку без понятного выхода. Поэтому: пробуем ПЕРЕКОДИРОВАТЬ
 * в JPEG прямо в браузере через canvas, и только если это не удалось —
 * отказываем с внятным текстом.
 *
 * Запрет при этом НИКУДА НЕ ДЕЛСЯ, он просто стоит последним: результат
 * конвертации проверяется теми же первыми байтами, и не-JPEG наружу не
 * уходит ни при каком исходе. Ограничение становится невидимым, а не мягким.
 *
 * ТИП ОПРЕДЕЛЯЕТСЯ ПО БАЙТАМ, А НЕ ПО ИМЕНИ И НЕ ПО `file.type`.
 * Расширение — это текст, который пишет человек. `file.type` браузер берёт
 * из системной таблицы MIME и у файлов из моста оболочки он бывает пустым
 * (см. useImagePicker: путь `window.algShell.pickFiles`). Единственный
 * честный признак — сигнатура: JPEG всегда начинается с FF D8 FF.
 *
 * ПРОЗРАЧНОСТЬ. У JPEG нет альфа-канала. Если не залить холст заранее,
 * прозрачные области станут ЧЁРНЫМИ (незаполненный холст = rgba(0,0,0,0),
 * а при сведении в JPEG нулевая альфа даёт чёрный). Поэтому холст
 * заливается белым ДО отрисовки — логотип на прозрачном фоне превращается
 * в логотип на белом, а не в чёрный квадрат.
 */
import { tr } from "../i18n";

/** Сигнатура JPEG: SOI (FF D8) + начало первого маркера (FF). */
const JPEG_MAGIC = [0xff, 0xd8, 0xff];

/**
 * Длинная сторона результата.
 *
 * Аватар нигде не показывается крупнее ~96 CSS-пикселей, в уведомлении —
 * и того меньше. 1024 берём с большим запасом на retina и на будущие
 * экраны просмотра, но оно же снимает вторую проблему: снимок с телефона
 * на 12 Мп в media-репозитории не нужен никому.
 */
const MAX_SIDE = 1024;

/** Качество JPEG. 0.9 — визуально без потерь на таком размере. */
const JPEG_QUALITY = 0.9;

/**
 * Что предлагать в системном диалоге выбора файла.
 *
 * ЭТО НЕ ЗАЩИТА, а подсказка диалогу: `accept` обходится перетаскиванием,
 * сменой фильтра в самом диалоге и мостом оболочки. Защита — проверка
 * байтов ниже. Здесь перечислено то, что мы УМЕЕМ принять (и при
 * необходимости перекодировать), а не то, что разрешено отправить.
 *
 * HEIC/HEIF оставлены сознательно: в Safari они декодируются штатно, в
 * Chromium — нет, и там человек получит внятный отказ вместо файла,
 * который не показывается в диалоге вовсе и выглядит как «пропал».
 */
export const AVATAR_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/bmp,image/heic,image/heif,image/avif";

/** Первые байты совпадают с сигнатурой JPEG? */
async function isJpegBytes(blob: Blob): Promise<boolean> {
  if (blob.size < JPEG_MAGIC.length) return false;
  const head = new Uint8Array(await blob.slice(0, JPEG_MAGIC.length).arrayBuffer());
  return JPEG_MAGIC.every((byte, i) => head[i] === byte);
}

/**
 * Разобрать файл в картинку.
 *
 * Сначала `createImageBitmap` — он декодирует вне основного потока и не
 * требует DOM. Если движок его не знает или не осилил формат, идём через
 * `<img>` + blob:-URL: у него шире охват (в Safari так открывается HEIC).
 * URL освобождается в обоих исходах, иначе blob висит до перезагрузки.
 */
async function decode(file: File): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close(),
      };
    } catch {
      // Молча падаем на запасной путь: причина неважна, важен результат.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(tr("avatar.decodeFailed")));
      el.src = url;
    });
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/** Холст -> JPEG-блоб. `toBlob` асинхронный и умеет вернуть null. */
function encode(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(tr("avatar.encodeFailed")))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

/** Имя с расширением .jpg — чтобы скачанный обратно файл не врал о себе. */
function jpegName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, "").trim();
  return `${base || "avatar"}.jpg`;
}

/** Перекодировать что угодно декодируемое в JPEG на белом фоне. */
async function toJpeg(file: File): Promise<File> {
  const { source, width, height, release } = await decode(file);
  try {
    if (!width || !height) throw new Error(tr("avatar.decodeFailed"));

    // Уменьшаем только если больше предела; апскейла не делаем никогда.
    const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error(tr("avatar.encodeFailed"));

    // БЕЛЫЙ ФОН ДО ОТРИСОВКИ — см. шапку модуля про прозрачность.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

    const blob = await encode(canvas);
    return new File([blob], jpegName(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    release();
  }
}

/**
 * ЕДИНСТВЕННАЯ ДВЕРЬ, через которую картинка попадает в аватар.
 *
 * Возвращает файл, про который ИЗМЕРЕНО, что он JPEG. Кидает ошибку с
 * готовым человеческим текстом, если этого добиться не удалось — вызов
 * setAvatarUrl/state-event в таком случае не должен состояться вовсе.
 *
 * Уже-JPEG проходит НАСКВОЗЬ, без перекодирования: второй проход по
 * lossy-кодеку только испортил бы картинку, а размер сервер и так держит
 * (`max_upload_size`), и ужимать чужой и без того валидный файл незачем.
 */
export async function ensureJpegAvatar(file: File): Promise<File> {
  if (await isJpegBytes(file)) return file;

  const converted = await toJpeg(file);

  // Выходной контроль. Даже после нашего же `toBlob("image/jpeg")`:
  // движок вправе не поддержать тип и молча отдать PNG — это описанное
  // поведение спецификации canvas, а не теория. Проверяем байты.
  if (!(await isJpegBytes(converted))) throw new Error(tr("avatar.jpegOnly"));

  return converted;
}
